import type { Session, SupabaseClient } from '@supabase/supabase-js';

/**
 * Вход по коду (решение владельца 24.10.2026, MFA_ENFORCEMENT_2026-10-24.md).
 *
 * У человека с подключённым кодом пропуск по одному паролю (aal1) серверы UNIA
 * не принимают — отвечают 401 с MFA_REQUIRED. Поэтому после пароля приложение
 * спрашивает шестизначный код из приложения-аутентификатора и только с пропуском
 * aal2 пускает человека внутрь.
 */

type Claims = { aal?: string; app_metadata?: { mfa_enabled?: unknown } };

function claimsOf(token: string): Claims | null {
  try {
    const part = token.split('.')[1] ?? '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Claims;
  } catch {
    return null;
  }
}

/**
 * Нужен ли код к этой сессии. Решается по самому пропуску, без запросов: внутри
 * onAuthStateChange звать методы supabase.auth нельзя (они ждут ту же блокировку).
 */
export function sessionNeedsCode(session: Session | null | undefined): boolean {
  if (!session?.access_token) return false;
  const claims = claimsOf(session.access_token);
  if (claims?.aal === 'aal2') return false;
  const flag = claims?.app_metadata?.mfa_enabled === true || session.user?.app_metadata?.mfa_enabled === true;
  const verifiedFactor = (session.user?.factors ?? []).some((f) => f.status === 'verified');
  return flag || verifiedFactor;
}

/** Проверить код и поднять сессию до aal2. Ошибка — готовым русским текстом. */
export async function verifyLoginCode(client: SupabaseClient, code: string): Promise<{ error: string | null }> {
  const digits = code.replace(/\D/g, '');
  if (digits.length !== 6) return { error: 'Введите 6 цифр из приложения' };
  try {
    const { data, error } = await client.auth.mfa.listFactors();
    if (error) return { error: 'Не получилось начать проверку кода. Проверьте связь и попробуйте ещё раз.' };
    const factor = data.totp.find((f) => f.status === 'verified') ?? data.totp[0];
    if (!factor) return { error: 'Код входа не найден. Откройте unia.one и проверьте настройки входа.' };
    const { error: verifyError } = await client.auth.mfa.challengeAndVerify({ factorId: factor.id, code: digits });
    if (verifyError) return { error: 'Код не подошёл. Возьмите свежий код из приложения и попробуйте ещё раз.' };
    return { error: null };
  } catch {
    return { error: 'Не получилось проверить код. Проверьте связь и попробуйте ещё раз.' };
  }
}

/** Ответ сервера «нужен код входа» (тело 401 содержит MFA_REQUIRED). */
export function isMfaRequiredBody(text: string | null | undefined): boolean {
  return typeof text === 'string' && text.includes('MFA_REQUIRED');
}

/**
 * Следить за ответами всех запросов: 401 с MFA_REQUIRED → onRequired() (показать
 * шаг кода), а не выход и не повторный вход. Возвращает функцию снятия.
 */
export function installMfaResponseObserver(onRequired: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return () => {};
  const original = window.fetch;
  const wrapped: typeof window.fetch = async (...args) => {
    const res = await original(...args);
    if (res.status === 401) {
      res.clone().text().then((t) => { if (isMfaRequiredBody(t)) onRequired(); }).catch(() => {});
    }
    return res;
  };
  window.fetch = wrapped;
  return () => { if (window.fetch === wrapped) window.fetch = original; };
}
